import { Inject, Injectable, Injector } from "@angular/core";
import { environment } from "../../environments/environment";
import { NotificationsService } from "./notifications/notifications.service";
import { Observable, Subject } from "rxjs";
import { AuthService } from "./auth.service";

export const SESSION_EXPIRED_MESSAGES = ["bookeng_app_token expired", "App token not valid"] as const;

export function isSessionExpiredErrorMessage(message: string | undefined): boolean {
    if (!message) {
        return false;
    }
    return SESSION_EXPIRED_MESSAGES.some((m) => message === m || message.includes(m));
}

export interface IErrorHandler {
    sendToBackend$: Observable<any>;

    handleError(error: Error, from?: string): void;
}

@Injectable({
    providedIn: "root",
})
export class CustomErrorHandlerService implements IErrorHandler {
    private notificationService: NotificationsService;
    private sendToBackend$$: Subject<Error> = new Subject<Error>();
    public sendToBackend$: Observable<Error> = this.sendToBackend$$.asObservable();

    constructor(@Inject(Injector) private injector: Injector) {
        this.notificationService = this.injector.get(NotificationsService);
    }

    handleError(error: Error, from?: string): void {
        const skipped_errors = [
            'NG0100',
            'ExpressionChangedAfterItHasBeenCheckedError',
            `Previous value for 'ng-pristine': 'true'. Current value: 'false'.`,
            'hide_consumers_manager_view',

            // 'ZoneAwareError',
        ]
        console.error({ error, from });

        if (skipped_errors.some((err: string) =>
            error?.message?.includes(err)
            // @ts-ignore
            ||  error?.originalStack?.includes(err))) {
            return
        }

        const sessionExpired = isSessionExpiredErrorMessage(error?.message);

        // Full refresh: reLogin and early socket calls (e.g. getAudioInputs) can race; server may briefly reject
        // with "App token not valid" even though the session is valid — do not force logout during grace window.
        if (sessionExpired) {
            try {
                const auth = this.injector.get(AuthService);
                if (auth.isWithinPostReloginGracePeriod()) {
                    return;
                }
            } catch {
                /* ignore */
            }
        }

        if (environment.logErrors) {
            this.sendToBackend$$.next(error);
        }

        if (sessionExpired) {
            this.notificationService.error("Session expired: You will be logged out!", true);
            setTimeout(
                // istanbul ignore next -- not coverable under Karma/Chrome: invoking this would
                // call window.location.reload(), which crashes the spec-runner browser context
                // (confirmed empirically elsewhere in this effort). Covered only by asserting the
                // timeout was scheduled with the right delay; the callback itself never runs.
                () => {
                    try {
                        this.injector.get(AuthService).logout();
                    } catch {
                        /* ignore */
                    }
                    window.location.reload();
                },
                5000,
            );
        } else {
            this.notificationService.error(error.message, true);
        }
    }
}
